import React, { useState, useEffect } from 'react';
import { 
  TrendingUp, Users, BookOpen, ClipboardList, FileQuestion,
  AlertTriangle, CheckCircle, Clock, Eye, ChevronDown, ChevronUp,
  Search, Filter, Download, BarChart3, Target, Award, GraduationCap
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function LMSAnalytics() {
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [students, setStudents] = useState([]);
  const [courses, setCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState('all');
  const [sortBy, setSortBy] = useState('needs_attention');
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedStudent, setExpandedStudent] = useState(null);
  const [studentDetails, setStudentDetails] = useState({});

  useEffect(() => {
    fetchData();
  }, [selectedCourse, sortBy]);

  const getHeaders = () => {
    const token = localStorage.getItem('token');
    return { 'Authorization': `Bearer ${token}` };
  };

  const fetchData = async () => {
    try {
      const headers = getHeaders();
      const courseParam = selectedCourse !== 'all' ? `?course_id=${selectedCourse}` : '';
      const studentParams = `?sort_by=${sortBy}${selectedCourse !== 'all' ? `&course_id=${selectedCourse}` : ''}`;

      const [overviewRes, studentsRes, coursesRes] = await Promise.all([
        fetch(`${API_URL}/api/lms/analytics/overview${courseParam}`, { headers }).then(r => r.json()).catch(() => null),
        fetch(`${API_URL}/api/lms/analytics/students${studentParams}`, { headers }).then(r => r.json()).catch(() => ({ students: [] })),
        fetch(`${API_URL}/api/courses`, { headers }).then(r => r.json()).catch(() => [])
      ]);

      setOverview(overviewRes);
      setStudents(studentsRes.students || []);
      setCourses(Array.isArray(coursesRes) ? coursesRes : coursesRes?.courses || []);
    } catch (error) {
      console.error('Error fetching analytics:', error);
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  const fetchStudentDetails = async (studentId) => {
    if (studentDetails[studentId]) {
      setExpandedStudent(expandedStudent === studentId ? null : studentId);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/lms/analytics/student/${studentId}`, {
        headers: getHeaders()
      });
      const data = await response.json();
      setStudentDetails(prev => ({ ...prev, [studentId]: data }));
      setExpandedStudent(studentId);
    } catch (error) {
      toast.error('Failed to load student details');
    }
  };

  const exportReport = () => {
    // Create CSV export
    const headers = ['Student ID', 'Name', 'Course', 'Content Progress', 'Assignment Progress', 'Quiz Score', 'Overall Progress', 'Status'];
    const rows = students.map(s => [
      s.student_id,
      s.student_name,
      s.course_name,
      `${s.content_progress}%`,
      `${s.assignment_progress}%`,
      `${s.avg_quiz_score}%`,
      `${s.overall_progress}%`,
      s.status
    ]);
    
    const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `student_progress_report_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    toast.success('Report exported successfully');
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'excellent': return 'bg-green-100 text-green-700 border-green-200';
      case 'on_track': return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'behind': return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      case 'needs_attention': return 'bg-red-100 text-red-700 border-red-200';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'excellent': return 'Excellent';
      case 'on_track': return 'On Track';
      case 'behind': return 'Behind';
      case 'needs_attention': return 'Needs Attention';
      default: return status;
    }
  };

  const getProgressColor = (progress) => {
    if (progress >= 75) return 'bg-green-500';
    if (progress >= 50) return 'bg-blue-500';
    if (progress >= 25) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  const filteredStudents = students.filter(s => 
    s.student_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.student_id?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const atRiskCount = students.filter(s => s.status === 'needs_attention').length;
  const excellentCount = students.filter(s => s.status === 'excellent').length;
  const onTrackCount = students.filter(s => s.status === 'on_track').length;
  const behindCount = students.filter(s => s.status === 'behind').length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="lms-analytics-page">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <BarChart3 className="h-7 w-7 text-indigo-600" />
            Student Progress Analytics
          </h1>
          <p className="text-gray-500 mt-1">Track and analyze student learning progress across all courses</p>
        </div>
        <Button onClick={exportReport} variant="outline" className="flex items-center gap-2">
          <Download className="h-4 w-4" />
          Export Report
        </Button>
      </div>

      {/* Overview Stats */}
      {overview && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4">
          <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
            <CardContent className="p-4 text-center">
              <Users className="h-6 w-6 mx-auto mb-1 text-blue-600" />
              <p className="text-xl font-bold text-blue-700">{overview.active_students || 0}</p>
              <p className="text-xs text-blue-600">Active Students</p>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-indigo-50 to-indigo-100 border-indigo-200">
            <CardContent className="p-4 text-center">
              <BookOpen className="h-6 w-6 mx-auto mb-1 text-indigo-600" />
              <p className="text-xl font-bold text-indigo-700">{overview.total_content || 0}</p>
              <p className="text-xs text-indigo-600">Total Content</p>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
            <CardContent className="p-4 text-center">
              <Eye className="h-6 w-6 mx-auto mb-1 text-green-600" />
              <p className="text-xl font-bold text-green-700">{overview.content_completions || 0}</p>
              <p className="text-xs text-green-600">Content Views</p>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
            <CardContent className="p-4 text-center">
              <ClipboardList className="h-6 w-6 mx-auto mb-1 text-purple-600" />
              <p className="text-xl font-bold text-purple-700">{overview.total_submissions || 0}</p>
              <p className="text-xs text-purple-600">Submissions</p>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200">
            <CardContent className="p-4 text-center">
              <FileQuestion className="h-6 w-6 mx-auto mb-1 text-orange-600" />
              <p className="text-xl font-bold text-orange-700">{overview.average_quiz_score || 0}%</p>
              <p className="text-xs text-orange-600">Avg Quiz Score</p>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200">
            <CardContent className="p-4 text-center">
              <Award className="h-6 w-6 mx-auto mb-1 text-emerald-600" />
              <p className="text-xl font-bold text-emerald-700">{excellentCount}</p>
              <p className="text-xs text-emerald-600">Top Performers</p>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-cyan-50 to-cyan-100 border-cyan-200">
            <CardContent className="p-4 text-center">
              <CheckCircle className="h-6 w-6 mx-auto mb-1 text-cyan-600" />
              <p className="text-xl font-bold text-cyan-700">{onTrackCount}</p>
              <p className="text-xs text-cyan-600">On Track</p>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-red-50 to-red-100 border-red-200">
            <CardContent className="p-4 text-center">
              <AlertTriangle className="h-6 w-6 mx-auto mb-1 text-red-600" />
              <p className="text-xl font-bold text-red-700">{atRiskCount}</p>
              <p className="text-xs text-red-600">Need Attention</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Status Distribution */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-gray-600">Progress Distribution</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex h-4 rounded-full overflow-hidden bg-gray-100">
            {students.length > 0 && (
              <>
                <div 
                  className="bg-green-500 transition-all" 
                  style={{ width: `${(excellentCount / students.length) * 100}%` }}
                  title={`Excellent: ${excellentCount}`}
                />
                <div 
                  className="bg-blue-500 transition-all" 
                  style={{ width: `${(onTrackCount / students.length) * 100}%` }}
                  title={`On Track: ${onTrackCount}`}
                />
                <div 
                  className="bg-yellow-500 transition-all" 
                  style={{ width: `${(behindCount / students.length) * 100}%` }}
                  title={`Behind: ${behindCount}`}
                />
                <div 
                  className="bg-red-500 transition-all" 
                  style={{ width: `${(atRiskCount / students.length) * 100}%` }}
                  title={`Needs Attention: ${atRiskCount}`}
                />
              </>
            )}
          </div>
          <div className="flex justify-between mt-2 text-xs">
            <span className="flex items-center gap-1"><span className="w-3 h-3 bg-green-500 rounded"></span> Excellent ({excellentCount})</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 bg-blue-500 rounded"></span> On Track ({onTrackCount})</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 bg-yellow-500 rounded"></span> Behind ({behindCount})</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 bg-red-500 rounded"></span> Needs Attention ({atRiskCount})</span>
          </div>
        </CardContent>
      </Card>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <Input
                placeholder="Search students by name or ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-students-input"
              />
            </div>
            <Select value={selectedCourse} onValueChange={setSelectedCourse}>
              <SelectTrigger className="w-full md:w-56" data-testid="course-filter">
                <SelectValue placeholder="All Courses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Courses</SelectItem>
                {courses.map(c => (
                  <SelectItem key={c.id} value={c.id}>{c.name || c.course_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-full md:w-56" data-testid="sort-filter">
                <SelectValue placeholder="Sort By" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="needs_attention">Needs Attention First</SelectItem>
                <SelectItem value="progress_asc">Progress (Low to High)</SelectItem>
                <SelectItem value="progress_desc">Progress (High to Low)</SelectItem>
                <SelectItem value="name">Name (A-Z)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Student Progress Table */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center justify-between">
            <span className="flex items-center gap-2">
              <GraduationCap className="h-5 w-5 text-indigo-600" />
              Student Progress Details ({filteredStudents.length} students)
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {filteredStudents.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Users className="h-16 w-16 mx-auto mb-4 opacity-50" />
              <p className="text-lg">No student progress data available</p>
              <p className="text-sm mt-2">Students will appear here once they start interacting with the LMS</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Student</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Course</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Content</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Assignments</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Quiz Avg</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Overall</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Details</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredStudents.map((student) => (
                    <React.Fragment key={student.student_db_id}>
                      <tr className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center flex-shrink-0">
                              {student.photo_url ? (
                                <img src={student.photo_url} alt="" className="w-full h-full rounded-full object-cover" />
                              ) : (
                                <span className="text-indigo-600 text-sm font-semibold">
                                  {student.student_name?.charAt(0) || '?'}
                                </span>
                              )}
                            </div>
                            <div>
                              <p className="font-medium text-gray-900 text-sm">{student.student_name}</p>
                              <p className="text-xs text-gray-500">{student.student_id}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">{student.course_name || '-'}</td>
                        <td className="px-4 py-3 text-center">
                          <div className="text-sm">
                            <span className="font-medium">{student.content_completed}</span>
                            <span className="text-gray-400">/{student.content_total}</span>
                          </div>
                          <div className="text-xs text-gray-500">{student.content_progress}%</div>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <div className="text-sm">
                            <span className="font-medium">{student.assignments_submitted}</span>
                            <span className="text-gray-400">/{student.assignments_total}</span>
                          </div>
                          <div className="text-xs text-gray-500">Avg: {student.avg_assignment_marks}</div>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className={`text-sm font-semibold ${student.avg_quiz_score >= 50 ? 'text-green-600' : 'text-red-600'}`}>
                            {student.avg_quiz_score}%
                          </span>
                          <div className="text-xs text-gray-500">{student.quizzes_passed} passed</div>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <div className="w-full max-w-[80px] mx-auto">
                            <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                              <div 
                                className={`h-full ${getProgressColor(student.overall_progress)}`}
                                style={{ width: `${student.overall_progress}%` }}
                              />
                            </div>
                            <p className="text-xs font-semibold mt-1">{student.overall_progress}%</p>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(student.status)}`}>
                            {getStatusLabel(student.status)}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => fetchStudentDetails(student.student_db_id)}
                          >
                            {expandedStudent === student.student_db_id ? (
                              <ChevronUp className="h-4 w-4" />
                            ) : (
                              <ChevronDown className="h-4 w-4" />
                            )}
                          </Button>
                        </td>
                      </tr>
                      
                      {/* Expanded Row */}
                      {expandedStudent === student.student_db_id && studentDetails[student.student_db_id] && (
                        <tr>
                          <td colSpan={8} className="px-4 py-4 bg-gray-50">
                            <div className="grid md:grid-cols-4 gap-4">
                              {/* Content Progress */}
                              <div className="bg-white p-4 rounded-lg border">
                                <h4 className="font-semibold text-sm mb-3 flex items-center gap-2">
                                  <BookOpen className="h-4 w-4 text-blue-600" />
                                  Content Completed
                                </h4>
                                <p className="text-2xl font-bold text-blue-600 mb-2">
                                  {studentDetails[student.student_db_id].content?.total_completed || 0}
                                </p>
                                {studentDetails[student.student_db_id].content?.completed?.slice(0, 3).map((c, i) => (
                                  <p key={i} className="text-xs text-gray-600 truncate">• {c.title}</p>
                                ))}
                              </div>
                              
                              {/* Assignment Progress */}
                              <div className="bg-white p-4 rounded-lg border">
                                <h4 className="font-semibold text-sm mb-3 flex items-center gap-2">
                                  <ClipboardList className="h-4 w-4 text-green-600" />
                                  Assignments
                                </h4>
                                <p className="text-2xl font-bold text-green-600 mb-2">
                                  Avg: {studentDetails[student.student_db_id].assignments?.average_marks || 0}
                                </p>
                                <div className="text-xs space-y-1">
                                  <p>Submitted: {studentDetails[student.student_db_id].assignments?.total_submitted || 0}</p>
                                  <p>Graded: {studentDetails[student.student_db_id].assignments?.graded_count || 0}</p>
                                </div>
                              </div>
                              
                              {/* Quiz Progress */}
                              <div className="bg-white p-4 rounded-lg border">
                                <h4 className="font-semibold text-sm mb-3 flex items-center gap-2">
                                  <FileQuestion className="h-4 w-4 text-purple-600" />
                                  Quizzes
                                </h4>
                                <p className="text-2xl font-bold text-purple-600 mb-2">
                                  {studentDetails[student.student_db_id].quizzes?.average_score || 0}%
                                </p>
                                <div className="text-xs space-y-1">
                                  <p>Attempted: {studentDetails[student.student_db_id].quizzes?.total_attempts || 0}</p>
                                  <p className="text-green-600">Passed: {studentDetails[student.student_db_id].quizzes?.passed_count || 0}</p>
                                </div>
                              </div>
                              
                              {/* Recent Activity */}
                              <div className="bg-white p-4 rounded-lg border">
                                <h4 className="font-semibold text-sm mb-3 flex items-center gap-2">
                                  <Clock className="h-4 w-4 text-gray-600" />
                                  Recent Activity
                                </h4>
                                <div className="space-y-2">
                                  {studentDetails[student.student_db_id].recent_activities?.slice(0, 3).map((a, i) => (
                                    <div key={i} className="text-xs flex items-center gap-2">
                                      {a.type === 'content_view' && <Eye className="h-3 w-3 text-blue-500" />}
                                      {a.type === 'assignment_submission' && <ClipboardList className="h-3 w-3 text-green-500" />}
                                      {a.type === 'quiz_attempt' && <FileQuestion className="h-3 w-3 text-purple-500" />}
                                      <span className="truncate flex-1">{a.title}</span>
                                    </div>
                                  )) || <p className="text-xs text-gray-500">No recent activity</p>}
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
